import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row } from 'antd';
import { Gap, Icon } from '@common/components';
import { Spacing } from 'works/legacy/lib/style-helper';
import { DateOfBirthLink, SsnLink } from '@clinician-profile/components';
import EditEmployeeId from 'works/components/edit-organization-profile/edit-employee-id';
import EditContractHoursRemaining from 'works/components/edit-organization-profile/edit-contract-hours-remaining';
import EditConversionDate from 'works/components/edit-organization-profile/edit-conversion-date';
import { ProfileStatusSetupLink } from '@working-experience/components';
import Auth from 'works/legacy/lib/auth';
import EditArrivalDate from 'works/components/edit-organization-profile/edit-arrival-date';
import { EditArrivalDateSupplier } from 'works/components/edit-organization-profile/edit-arrival-date-supplier';
import { formatPhone } from 'works/legacy/services/phone-service';

export default function CandidateAssignmentContact({ assignment }) {
  const organizationProfile = assignment.organizationProfile;

  return (
    <div className="CandidateAssignmentContact">
      <Row gutter={[Spacing.baseSpacing, 4]}>
        <Col span={5}>
          <div>
            {Auth.organizationMember() &&
              assignment.worksProfile.identificationCode && (
                <ProfileStatusSetupLink
                  assignmentId={assignment?.id}
                  doNotRehire={assignment?.doNotRehire}
                  profileIdentificationCode={
                    assignment.worksProfile.identificationCode
                  }
                />
              )}
          </div>

          <Gap align="center" className="sentry-mask">
            <Icon
              icon="mobile"
              kind="light"
              size="sm"
              tooltipText="Mobile Number"
            />
            {formatPhone(assignment.worksProfile.phone) || 'N/A'}
          </Gap>

          {assignment.worksProfile.email && (
            <Gap align="center" className="sentry-mask">
              <Icon
                icon="envelope"
                kind="light"
                size="sm"
                tooltipText="Email"
              />
              {assignment.worksProfile.email}
            </Gap>
          )}
        </Col>

        <Col span={5}>
          <Gap align="center">
            <Icon
              icon="building"
              kind="light"
              size="sm"
              tooltipText="Supplier"
            />
            {assignment.supplier.name}
          </Gap>
          {assignment.worksProfile.canShowPhi.value && (
            <Gap align="center" className="sentry-mask">
              <Icon
                icon="birthdayCake"
                kind="light"
                size="sm"
                tooltipText="Birthdate"
              />
              <DateOfBirthLink worksProfileId={assignment.worksProfile.id} />
            </Gap>
          )}

          {assignment.worksProfile.canShowPhi.value && (
            <Gap align="center" className="sentry-mask">
              <Icon
                icon="idCard"
                kind="light"
                size="sm"
                tooltipText="Social Security Number"
              />
              <SsnLink
                worksProfileId={assignment.worksProfile.id}
                canShowPhi={assignment.worksProfile.canShowPhi.value}
              />
            </Gap>
          )}
        </Col>

        <Col span={5}>
          <div>
            {organizationProfile.canUpdate.value && (
              <EditEmployeeId
                employeeId={organizationProfile.employeeId}
                organizationProfileId={organizationProfile.id}
                canUpdate={organizationProfile.canUpdate.value}
              />
            )}
          </div>

          <EditConversionDate
            isInternational={organizationProfile.worksProfile.isInternational}
            conversionDate={organizationProfile.conversionDate}
            estimatedConversionDate={
              organizationProfile.estimatedConversionDate
            }
            organizationProfileId={organizationProfile.id}
            canUpdate={organizationProfile.canUpdate.value}
          />
          <Row>
            {/* Organization Est Arrival (can update entire profile on link) */}
            {organizationProfile.worksProfile.isInternational &&
              !organizationProfile.canUpdateArrivalDates.value && (
                <EditArrivalDate
                  estimatedArrivalDate={
                    organizationProfile.estimatedArrivalDate
                  }
                  arrivedAtDate={organizationProfile.arrivedAtDate}
                  organizationProfileId={organizationProfile.id}
                  canUpdate={organizationProfile.canUpdate.value}
                />
              )}

            {/* Supplier Est Arrival  (can only update this one field) */}
            {organizationProfile.canUpdateArrivalDates.value &&
              !organizationProfile.canUpdate.value &&
              organizationProfile.worksProfile.isInternational && (
                <EditArrivalDateSupplier
                  estimatedArrivalDate={
                    organizationProfile.estimatedArrivalDate
                  }
                  arrivedAtDate={organizationProfile.arrivedAtDate}
                  organizationProfileId={organizationProfile.id}
                  canUpdateArrivalDates={
                    organizationProfile.canUpdateArrivalDates.value
                  }
                />
              )}
          </Row>
        </Col>

        <Col span={5}>
          {organizationProfile.worksProfile.isInternational && (
            <EditContractHoursRemaining
              contractHoursRemaining={
                organizationProfile.contractHoursRemaining
              }
              organizationProfileId={organizationProfile.id}
              canUpdate={organizationProfile.canUpdate.value}
            />
          )}
        </Col>
      </Row>
    </div>
  );
}

CandidateAssignmentContact.propTypes = {
  assignment: PropTypes.shape({
    id: PropTypes.string.isRequired,
    doNotRehire: PropTypes.bool.isRequired,
    supplier: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
    worksProfile: PropTypes.shape({
      id: PropTypes.string.isRequired,
      fullName: PropTypes.string.isRequired,
      phone: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
      identificationCode: PropTypes.string.isRequired,
      canShowPhi: PropTypes.shape({
        value: PropTypes.bool.isRequired,
      }),
    }),
    staffingRequestApplication: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    organizationProfile: PropTypes.shape({
      id: PropTypes.string.isRequired,
      employeeId: PropTypes.string,
      estimatedConversionDate: PropTypes.string,
      conversionDate: PropTypes.string,
      estimatedArrivalDate: PropTypes.string,
      arrivedAtDate: PropTypes.string,
      contractHoursRemaining: PropTypes.number,
      worksProfile: PropTypes.shape({
        id: PropTypes.string.isRequired,
        isInternational: PropTypes.bool.isRequired,
      }).isRequired,
      canUpdate: PropTypes.shape({
        value: PropTypes.bool.isRequired,
      }),
      canUpdateArrivalDates: PropTypes.shape({
        value: PropTypes.bool.isRequired,
      }),
    }),
  }),
};
